/**
 * API Proxy — the single point of contact between React components and the backend.
 *
 * Rules (architectural prohibition #2):
 *  - No React component may call the Gemini API directly.
 *  - All AI-powered calls go through this module.
 *  - Audio decode lives here too.
 *
 * Backend REST calls go through the Express API at /api/v1/*.
 * Firebase Functions calls (if any) go through httpsCallable.
 */

import { auth } from '@/config/firebase';
import type { CEFRLevel } from '@/constants/cefr';
import { t } from '@/i18n/t';
import { beginLessonMedia, touchLessonMedia } from '@/core/lessonMediaCache';

// Round 31, A4 — from/to are CEFRLevel (base CEFR only, e.g. "A1"/"A2"),
// never a sub-level/variation string like "A1.1" — matches the backend's
// own SkillLevelChange (progression.types.ts), whose from/to are CEFRBase.
// Previously typed as bare `string` here, which is what let a hand-seeded
// test fixture with sub-level values (Round 29/30's own throwaway
// verification scripts) render without any type error catching it. See
// the Round 31 report for the full trace — the backend was never wrong;
// this frontend type was just loose enough not to notice if it had been.
export interface LevelAdvancementEntry {
  skill: string;
  from: CEFRLevel;
  to: CEFRLevel;
  direction: 'up' | 'down';
}
import type {
  StartScaffoldingResult,
  RecordAttemptResult,
  EvalStageResult,
  ReadingSubmission,
} from '@/types/reading';
import type {
  StartTestResult,
  SubmitResponseResult,
  FinalTestResult,
  PlacementAttemptSummary,
  PlacementProgress,
  SkillType,
} from '@/types/placement';

import { ADMIN_MFA_REQUIRED_EVENT } from '@/pages/admin/adminErrors';
import { notifyPendingDeletion } from './pendingDeletion';
import { previewPrefix } from '@/core/previewMode';
import { trackFirstCall } from '@/core/warmingNotice';
import { isRateLimitedBody, notifyRateLimited, notifyIfRateLimited, parseRetryAfterSeconds } from '@/core/rateLimit';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// ── HTTP helpers ──────────────────────────────────────────────────────────────

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error(t('errors:apiProxy.notAuthenticated'));
  return user.getIdToken();
}

// Block 63 — apiFetch used to throw a plain Error, so callers had no
// reliable way to distinguish e.g. a 409 "already started" conflict (benign,
// recoverable — the caller should fetch existing state) from a real failure
// (500, network error — nothing to recover, don't fabricate progress).
// String-matching the error message was the only alternative and is fragile
// (the same trap CLAUDE.md documents placementTestController having hit
// before its own statusCode check was added). Carries the real HTTP status
// so callers can check `err instanceof ApiFetchError && err.status === 409`.
export class ApiFetchError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    // Retake mechanism (block RM4) — carries the response body's `data` and
    // `code` for a structured-refusal response (e.g. RETAKE_OFFER_PENDING),
    // which is genuinely different from an opaque failure: the caller needs
    // the payload to offer a real choice, not just a message to display.
    // Optional and unread by every existing caller, so this is additive.
    public readonly code?: string,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiFetchError';
  }
}

// Exported (Round 113) so the admin lesson loader's own lazy chunk can define its API calls itself instead of
// adding them to this file, which every learner page loads.
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getIdToken();
  // Round 136b: a slow FIRST answer (the server waking up) shows "Getting things ready..." (core/warmingNotice.ts)
  const res = await trackFirstCall(fetch(`${API_BASE}${previewPrefix()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  }));

  const json = await res.json();
  // Round 108, C4 — the limiter's 429 raises a window event so ONE app-level
  // banner (RateLimitNotice) tells the learner, even when the calling screen
  // swallows the error (guiding questions, image slots) or shows its own.
  if (isRateLimitedBody(res.status, json)) notifyRateLimited(parseRetryAfterSeconds(json, res.headers.get('Retry-After')));
  if (!res.ok || !json.success) {
    // Round 106, GAP 2a — the backend refuses an admin session that carries
    // no TOTP claim with code MFA_REQUIRED (adminAuth.ts requireRole). Every
    // admin call funnels through here, so one dispatch covers every page,
    // including ones that swallow their own errors; AdminLayout listens.
    if (json.code === 'MFA_REQUIRED' && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(ADMIN_MFA_REQUIRED_EVENT));
    }
    // Round 139 - the account is scheduled for deletion: one app-level screen offers "Keep my account" (PendingDeletionScreen)
    if (json.code === 'ACCOUNT_PENDING_DELETION') notifyPendingDeletion(json.data);
    throw new ApiFetchError(json.error || `HTTP ${res.status}`, res.status, json.code, json.data);
  }
  return json.data as T;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

// Round 141 - an e-mail-and-password account confirms its address through OUR e-mail (Resend), sent by the server. 'throttled' carries the wait;
// the server answers 429 EMAIL_SEND_LIMIT for that, turned into the same outcome here so the page needs one branch.
export type EmailConfirmationOutcome = { outcome: 'sent' | 'already_confirmed' | 'throttled' | 'send_failed'; retryInSeconds?: number };

export async function sendEmailConfirmation(lang: string): Promise<EmailConfirmationOutcome> {
  try {
    return await apiFetch<EmailConfirmationOutcome>('/auth/email-confirmation/send', { method: 'POST', body: JSON.stringify({ lang: lang.startsWith('ar') ? 'ar' : 'en' }) });
  } catch (e) {
    if (e instanceof ApiFetchError && e.code === 'EMAIL_SEND_LIMIT') return { outcome: 'throttled', retryInSeconds: Number((e.data as any)?.retryInSeconds) || undefined };
    throw e;
  }
}

/** Round 141 - corrects the address of an account that has not confirmed it yet (the server refuses a confirmed or a Google account). */
export async function changeUnconfirmedEmail(email: string, lang: string): Promise<EmailConfirmationOutcome> {
  return apiFetch<EmailConfirmationOutcome>('/auth/email-change', { method: 'POST', body: JSON.stringify({ email: email.trim(), lang: lang.startsWith('ar') ? 'ar' : 'en' }) });
}

// ── Trial Activation ──────────────────────────────────────────────────────────

export async function activateTrial(): Promise<{ status: string; trialCefrLevel: string }> {
  return apiFetch('/auth/activate-trial', { method: 'POST' });
}

// ── Placement Test ────────────────────────────────────────────────────────────

export interface PlacementQuestion {
  id: string;
  questionText: string;
  options?: string[];
  skill: string;
  cefrLevel: string;
}

// ── Placement Test — IRT adaptive ────────────────────────────────────────────
export async function startPlacementTest(skill: string): Promise<StartTestResult> {
  return apiFetch<StartTestResult>('/placement-test/start', {
    method: 'POST',
    body: JSON.stringify({ skill }),
  });
}

export async function submitPlacementResponse(payload: {
  attemptId: string;
  itemId: string;
  response: string;
  responseTimeMs: number;
  audioBase64?: string;
  audioMimeType?: string;
}): Promise<SubmitResponseResult> {
  return apiFetch<SubmitResponseResult>('/placement-test/submit-response', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getPlacementAttemptResult(attemptId: string): Promise<FinalTestResult> {
  return apiFetch<FinalTestResult>(`/placement-test/results/${attemptId}`);
}

export async function getPlacementAttempts(): Promise<PlacementAttemptSummary[]> {
  return apiFetch<PlacementAttemptSummary[]>('/placement-test/attempts');
}

/** Round 133 (R133-1): the placement hub - per skill done / in progress / not started, answers saved, the progress bar's own length estimate. */
export async function getPlacementProgress(): Promise<PlacementProgress> {
  return apiFetch<PlacementProgress>('/placement-test/progress');
}

// ── Pilot measurement (Round 134, R134-5): the optional writing-sample consent and "How did that feel?" ────────────────────────────────

/** The optional consent shown at the end of the placement writing section. Sends only the learner's choice. */
export async function setPlacementWritingConsent(consent: boolean): Promise<{ writingSample: boolean }> {
  return apiFetch<{ writingSample: boolean }>('/placement-test/writing-consent', { method: 'POST', body: JSON.stringify({ consent }) });
}

export type LevelFeelAnswer = 'too_easy' | 'about_right' | 'too_hard' | 'skipped';

/** Which skills the server says are due for "How did that feel?" (after that skill's 5th completed lesson, once). */
export async function getLevelFeedbackPending(): Promise<SkillType[]> {
  return (await apiFetch<{ pending: SkillType[] }>('/level-feedback')).pending ?? [];
}

export async function sendLevelFeedback(skill: SkillType, answer: LevelFeelAnswer): Promise<void> {
  await apiFetch('/level-feedback', { method: 'POST', body: JSON.stringify({ skill, answer }) });
}

// ── Specialty requests (Round 133, R133-3): "My specialty isn't listed" ────────────────────────────────────────────

export interface MySpecialtyRequest { id: string; text: string; status: 'open' | 'added' | 'withdrawn'; createdAt: string; updatedAt: string }

export async function getMySpecialtyRequest(): Promise<MySpecialtyRequest | null> {
  const r = await apiFetch<{ request: MySpecialtyRequest | null }>('/specialty-requests/mine');
  return r.request;
}
/** creates the request, or edits the open one (one open request per learner). The text is data only. */
export async function saveSpecialtyRequest(text: string): Promise<MySpecialtyRequest> {
  const r = await apiFetch<{ request: MySpecialtyRequest }>('/specialty-requests', { method: 'POST', body: JSON.stringify({ text }) });
  return r.request;
}
export async function withdrawSpecialtyRequest(): Promise<boolean> {
  const r = await apiFetch<{ withdrawn: boolean }>('/specialty-requests/mine', { method: 'DELETE' });
  return r.withdrawn;
}

// Round 51, B1 — skipPlacementSkill removed along with /skip-skill. A
// microphone is now required to complete the speaking placement test.

// ── Lessons ───────────────────────────────────────────────────────────────────

/** Round 128 (C8c) - where a lesson stands in the learner's block sequence. */
export type LessonSequenceState = 'done' | 'next' | 'in_progress' | 'retake_pending' | 'locked';

export interface LessonSummary {
  id: string;
  title: string;
  /** BASE level only ("A2") - the server never sends the .1/.2 half (Round 128). */
  cefrLevel: string;
  subjectArea?: string;
  lessonIndex: number;
  published?: boolean;
  /** Round 128 - 1..120 position in the block sequence, and the lesson's state in the learner's current attempt */
  position: number;
  state: LessonSequenceState;
  isCompleted: boolean;
}

/** Round 128 - the learner's block as one sequence: "<doneCount> of <total> done in <level>" and the lesson to start next. */
export interface LessonBlockInfo {
  level: string | null;
  attempt: number;
  doneCount: number;
  total: number;
  nextLessonId: string | null;
  nextPosition: number | null;
}

export interface LessonsResult {
  lessons: LessonSummary[];
  fieldId: string | null;
  noSpecialtySelected: boolean;
  block: LessonBlockInfo | null;
  /** Round 132b - why the list is empty: 'no_specialty' | 'unknown_field' | 'no_lessons_at_level' (null when it is not) */
  emptyReason: 'no_specialty' | 'unknown_field' | 'no_lessons_at_level' | null;
  fieldName: string | null;
}

export async function getLessons(): Promise<LessonsResult> {
  const result = await apiFetch<any>('/lessons');
  const arr: any[] = Array.isArray(result) ? result : (result.lessons ?? []);
  return {
    // Normalise: backend may return `lessonId` instead of `id`
    lessons: arr.map(l => ({ ...l, id: l.id ?? l.lessonId })),
    fieldId: result?.fieldId ?? null,
    noSpecialtySelected: result?.noSpecialtySelected ?? false,
    block: result?.block ?? null,
    emptyReason: result?.emptyReason ?? null,
    fieldName: result?.fieldName ?? null,
  };
}

// ── Sessions ──────────────────────────────────────────────────────────────────

export async function startSession(lessonId: string) {
  const data = await apiFetch<{ sessionId: string; currentPhase: string; reviewOnly?: boolean }>(
    `/lessons/${lessonId}/start`,
    { method: 'POST' },
  );
  // Round 110, B3 — lesson media is cached per lesson (core/lessonMediaCache.ts). A START that
  // succeeded makes this the current lesson and deletes every other lesson's cached files; the
  // same lesson id (resume / reopen / retake) keeps them. A refused start (retake offer pending,
  // access denied) throws above, so the retake offer's lesson keeps its cache. Review mode only
  // VIEWS a finished lesson, so it evicts nothing.
  if (data?.reviewOnly) touchLessonMedia(lessonId);
  else beginLessonMedia(lessonId);
  return data;
}

// Retake mechanism (block RM4) — the confirmation's "forfeit" choice.
// Moment 2 (Nate's sequencing ruling) — the response now carries
// levelAdvancement (previously a bare {success:true}); see the controller's
// own comment for why. Returns it directly, not void, so the caller can
// show it before moving on to the next lesson.
export async function forfeitRetakeOffer(): Promise<{ levelAdvancement?: LevelAdvancementEntry[] }> {
  const res = await apiFetch<{ levelAdvancement?: LevelAdvancementEntry[] }>('/lessons/retake-offer/forfeit', { method: 'POST' });
  return res ?? {};
}

// Round 31, B3 — re-surfacing. profile.pendingRetakeOffer (read via
// AuthContext) only signals THAT an offer exists; this fetches the full
// detail (weakPhases) needed to actually render the announcement again.
export interface PendingRetakeOffer {
  sessionId: string;
  lessonId: string;
  weakPhases: string[];
}

export async function getPendingRetakeOffer(): Promise<PendingRetakeOffer | null> {
  const res = await apiFetch<{ offer: PendingRetakeOffer | null }>('/lessons/retake-offer/pending');
  return res?.offer ?? null;
}

// EF2 — the dismissal gap. Checked on completion-screen mount so a level
// change the learner navigated away from before dismissing still reaches
// them. sessionIds is every session this combined batch came from, one
// acknowledge call per id on dismiss.
export async function getUnacknowledgedLevelAdvancement(): Promise<{
  levelAdvancement: LevelAdvancementEntry[];
  sessionIds: string[];
}> {
  const res = await apiFetch<{ levelAdvancement: LevelAdvancementEntry[]; sessionIds: string[] }>(
    '/sessions/level-advancement/unacknowledged',
  );
  return res ?? { levelAdvancement: [], sessionIds: [] };
}

export async function acknowledgeLevelAdvancement(sessionId: string): Promise<void> {
  await apiFetch<void>(`/sessions/${sessionId}/level-advancement/acknowledge`, { method: 'POST' });
}

// Round 32, D3/D4 — the block-outcome counterpart, mirroring
// getUnacknowledgedLevelAdvancement/acknowledgeLevelAdvancement exactly.
export interface BlockOutcome {
  type: 'advance' | 'redo' | 'mastered_at_ceiling' | 'dropped' | 'demoted';
  fromBlock: CEFRLevel;
  toBlock?: CEFRLevel;
  /** Round 128 - the skills the Round 42 valve moved down (served levels before -> after); absent for the across-the-board drop. */
  demotedSkills?: { skill: 'reading' | 'listening' | 'writing' | 'speaking'; from: CEFRLevel; to: CEFRLevel }[];
  newAttempt?: number;
}

export async function getUnacknowledgedBlockOutcome(): Promise<{
  blockOutcomes: BlockOutcome[];
  sessionIds: string[];
}> {
  const res = await apiFetch<{ blockOutcomes: BlockOutcome[]; sessionIds: string[] }>(
    '/sessions/block-outcome/unacknowledged',
  );
  return res ?? { blockOutcomes: [], sessionIds: [] };
}

export async function acknowledgeBlockOutcome(sessionId: string): Promise<void> {
  await apiFetch<void>(`/sessions/${sessionId}/block-outcome/acknowledge`, { method: 'POST' });
}

// Round 23, KK3 — first real consumer of this endpoint (checked: zero
// callers anywhere in the frontend before now). Typed narrowly for the one
// field the writing-phase scaffolding panel needs; the endpoint returns
// more (reading/listening/writing/speaking PhaseSummary, etc.) but nothing
// else has a consumer yet, so not declared here.
export interface SessionState {
  scaffoldingStrategy: { mode: 'proactive' | 'standard' | 'minimal' } | null;
}

export async function getSessionState(sessionId: string) {
  return apiFetch<SessionState>(`/sessions/${sessionId}/state`);
}

export async function abandonSession(sessionId: string) {
  return apiFetch<void>(`/sessions/${sessionId}/abandon`, { method: 'POST' });
}

export async function getActiveSession(): Promise<any | null> {
  try {
    const result = await apiFetch<{ session: any }>('/sessions/active');
    return result.session;
  } catch (e) {
    // 404 or no active session is not an error
    return null;
  }
}

// Block S9, Part A — widened to expose session.speaking's persisted
// composite. The real server response (session.controller.ts's
// completePhase: `data: result` where result = {session, lessonComplete})
// always carried this; this type just never asked for it. `nextPhase` is
// kept even though no real response field matches it (checked: nothing in
// the frontend reads it either) — additive widening only, not a cleanup.
export async function completePhase(
  sessionId: string,
  phase: 'reading' | 'listening' | 'writing' | 'speaking',
) {
  // Round 129 (owner ruling 2026-10-22): NO body. The server holds every graded answer and derives the score, the count and the
  // duration itself; a score or summary in the request is a 400 FORBIDDEN_CLIENT_FIELD. 409 PHASE_INCOMPLETE = not every question is answered.
  return apiFetch<{
    nextPhase: string | null;
    session?: {
      reading?:   { compositeScore?: number | null } | null;
      listening?: { compositeScore?: number | null } | null;
      speaking?:  { compositeScore?: number | null; textPassComposite?: number | null } | null;
    };
  }>(
    `/sessions/${sessionId}/phases/${phase}/complete`,
    { method: 'POST' },
  );
}

// Round 36, YY3 — POST /sessions/:id/complete. The ONLY route that runs
// streak/lessonsCompleted, Mechanism 1/2 (applyProgressionUpdate),
// unlockNextLesson, level-ceiling advancement, and vocabulary collection —
// see session.service.ts's completeSession. Had zero frontend callers
// before now (confirmed: grepped the whole frontend for any call to
// /complete other than /phases/:phase/complete — none existed). Loosely
// typed to match this file's existing getActiveSession/startSession
// convention (any) — the real shape is session.types.ts's
// SessionCompletionResponse.
export async function completeSession(sessionId: string): Promise<any> {
  return apiFetch<any>(`/sessions/${sessionId}/complete`, { method: 'POST' });
}

// ── Reading Phase ─────────────────────────────────────────────────────────────

export async function requestImageForSlot(lessonId: string, slotId: string) {
  return apiFetch<{ publicUrl: string | null }>(
    `/lessons/${lessonId}/images/${slotId}`,
  );
}

// ── Reading Phase — Scaffolding (Phase 7 contract) ───────────────────────────

export async function startScaffolding(
  sessionId: string,
  phase: 'reading' | 'listening',
): Promise<StartScaffoldingResult> {
  return apiFetch<StartScaffoldingResult>(
    `/sessions/${sessionId}/phases/${phase}/scaffolding/start`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// Round 155d - the vocabulary matching step between reading and listening. The server sends each set as two separately shuffled lists
// (words, definitions) with opaque ids and grades the learner's OWN pairs; nothing here carries an accuracy, a count or a correct flag.
export interface VocabStepSet {
  setId: string;
  index: number;
  words: { id: string; text: string }[];
  definitions: { id: string; text: string }[];
  answered: boolean;
  /** only for a set already answered (resume after a reload) */
  results?: VocabSetResult[];
}
export interface VocabSetResult { wordId: string; chosenDefinitionId: string; correctDefinitionId: string; correct: boolean }
export type VocabStepView =
  | { required: false; status: 'not_required' }
  | { required: true; status: 'done' }
  | { required: true; status: 'in_progress'; attempt: number; maxAttempts: number; totalSets: number; sets: VocabStepSet[] };
export interface VocabAnswerResult {
  setId: string;
  results: VocabSetResult[];
  attemptComplete: boolean;
  outcome?: 'continue' | 'retry';
  done?: boolean;
}

export async function startVocabStep(sessionId: string): Promise<VocabStepView> {
  return apiFetch<VocabStepView>(`/sessions/${sessionId}/vocab-step/start`, { method: 'POST', body: JSON.stringify({}) });
}

export async function answerVocabStep(sessionId: string, setId: string, pairs: { wordId: string; definitionId: string }[]): Promise<VocabAnswerResult> {
  return apiFetch<VocabAnswerResult>(`/sessions/${sessionId}/vocab-step/answer`, { method: 'POST', body: JSON.stringify({ setId, pairs }) });
}

// Block 63 — resume path: fetches the existing state + full activity list
// for a phase already started, for the case startScaffolding's 409 means
// "recover this", not "this phase failed". See Phase1Listening.tsx /
// Phase2Reading.tsx for the call site.
export async function getScaffoldingState(
  sessionId: string,
  phase: 'reading' | 'listening',
): Promise<StartScaffoldingResult> {
  return apiFetch<StartScaffoldingResult>(
    `/sessions/${sessionId}/phases/${phase}/scaffolding/state`,
  );
}

export async function recordAttempt(
  sessionId: string,
  phase: 'reading' | 'listening',
  activityId: string,
  submission: ReadingSubmission,
  reveal = false,
): Promise<RecordAttemptResult> {
  return apiFetch<RecordAttemptResult>(
    `/sessions/${sessionId}/phases/${phase}/scaffolding/attempt`,
    { method: 'POST', body: JSON.stringify({ activityId, submission, reveal }) },
  );
}

// Reading only. Called AFTER recordAttempt has already returned a wrong-answer
// result — a separate request so scoring stays fast. Always resolves to
// { questions: string[] } (never throws for the caller to handle specially —
// the backend degrades any internal failure to an empty array).
export async function getGuidingQuestion(
  sessionId: string,
  activityId: string,
  itemId: string,
  chosenAnswer: string,
): Promise<{ questions: string[] }> {
  return apiFetch<{ questions: string[] }>(
    `/sessions/${sessionId}/phases/reading/scaffolding/guiding-question`,
    { method: 'POST', body: JSON.stringify({ activityId, itemId, chosenAnswer }) },
  );
}

export async function evalStage(
  sessionId: string,
  phase: 'reading' | 'listening',
): Promise<EvalStageResult> {
  return apiFetch<EvalStageResult>(
    `/sessions/${sessionId}/phases/${phase}/scaffolding/eval`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// Stage 2b-close FIX A: now returns { state, activities } — the 2 new remediation
// activities' client-safe content, same shape as startScaffolding.
export async function startStage4(
  sessionId: string,
  phase: 'reading' | 'listening',
): Promise<StartScaffoldingResult> {
  return apiFetch<StartScaffoldingResult>(
    `/sessions/${sessionId}/phases/${phase}/scaffolding/stage4`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// ── Listening Phase ───────────────────────────────────────────────────────────

export interface ListeningPhaseData {
  audioUrl: string;
  activities: Array<{
    id: string;
    questionText: string;
    options?: string[];
    responseType: string;
  }>;
}

export async function startListeningPhase(sessionId: string): Promise<ListeningPhaseData> {
  return apiFetch<ListeningPhaseData>(`/sessions/${sessionId}/phase/listening/start`);
}

// ── Writing Phase ────────────────────────────────────────────────────────────

// Round 24, LL1 — first real caller of this endpoint. sessionService.
// startWritingPhase (session.service.ts) has always existed and always
// worked when called directly, but nothing in the live frontend ever called
// it (checked: same true of startListeningPhase above and the reading
// equivalent — none of the three per-phase start endpoints had a frontend
// caller before this). The response itself is redundant with what
// toLessonDTO already gives the frontend (taskPrompt/wordCountMin/audience)
// — this call's real purpose is its SIDE EFFECT: persisting
// servedLevel.writing on the session, which submitWritingResponse's
// resolveServedLevel(session, 'writing') needs to read back at grading time
// instead of falling through to session.cefrLevel.
export interface WritingPhaseStartData {
  sessionId: string;
  taskPrompt: string;
  wordCountMin: number;
  audience: string | null;
}

export async function startWritingPhase(sessionId: string): Promise<WritingPhaseStartData> {
  // Round BG2 — was `/phase/writing/start` with no method (GET by fetch's
  // default), 404ing against the backend's actual registration: POST
  // `/phases/:phase/start` (session.routes.ts), the same shared, plural
  // route reading/listening's own real endpoints (scaffolding/start,
  // phases/reading|listening/complete) already use correctly. This 404 was
  // silent (see the .catch() at this call's one call site, LessonPage.tsx)
  // — the side effect this whole call exists for (persisting
  // servedLevel.writing) never happened for any real submission.
  return apiFetch<WritingPhaseStartData>(`/sessions/${sessionId}/phases/writing/start`, { method: 'POST' });
}

// ── Writing Phase ─────────────────────────────────────────────────────────────

// Round 3, F3 — this type previously did not match what the backend
// actually returns (score/cefrProfile/errorClusters were all wrong shapes;
// apiFetch does no remapping, so the mismatch was silent until something
// tried to render the raw values — see WritingScoreCard/ErrorClusterList).
// Field shapes below are the real backend response, confirmed live.

// Round 14, AA2 — hand-mirrors src/types/writingErrorTaxonomy.ts's
// ErrorCluster/ErrorFrequency (backend-only, not importable here).
export type ErrorFrequency = 'isolated' | 'occasional' | 'recurrent' | 'systematic' | 'pervasive';

// Round 16, DD1 — an instance is a correction pair, not a bare quote.
export interface ErrorInstance {
  original: string;
  corrected: string;
}

// Round 15, BB2 — this is the ENRICHED shape session.controller.ts's
// submitWritingResponse actually sends (displayName/gloss/learnerLabel
// looked up server-side from the taxonomy at serialisation, never
// stored). Storage keeps id only — but nothing in this file reads storage
// directly, so this interface can just describe the wire shape without a
// separate "raw" variant.
// Round 19, GG4 — how much this specific occurrence interferes with
// understanding (model-judged per instance, not per taxonomy id). Feeds
// the Primary Focus cascade server-side; not currently rendered anywhere.
export type MeaningImpact = 'obscures' | 'noticeable' | 'cosmetic';

export interface ErrorCluster {
  id: string;
  displayName: string;
  // Round 16, DD4 — the learner-facing name. This is what the UI renders
  // (ErrorClusterList.tsx) — displayName stays for internal/analytic use.
  learnerLabel: string;
  gloss: string;
  frequency: ErrorFrequency;
  meaningImpact: MeaningImpact;
  instances: ErrorInstance[];
  // Round 16, DD1 — 1-3 sentences of plain-language explanation.
  feedback: string;
}

export interface WritingEvaluation {
  compositeScore: number;             // 0.0-1.0, NOT 0-100
  cefrProfile: {
    ta: string; lra: string; cc: string; ar: string; tv: string;
  };
  dimensionScores: {
    ta: number;   // Task Achievement       — 0.0-1.0
    lra: number;  // Lexical Resource & Accuracy
    cc: number;   // Coherence & Cohesion
    ar: number;   // Accuracy & Range
    tv: number;   // Task-specific Vocabulary
  };
  // Round 14, AA2 — each bucket is ErrorCluster[], not string[] (round 13).
  // This interface hand-mirrors the backend response (see the round-3
  // comment above) rather than importing src/types/writingErrorTaxonomy.ts,
  // which is backend-only — kept in sync manually, same as the rest of
  // this interface.
  errorClusters: {
    language: ErrorCluster[];
    discourse: ErrorCluster[];
    // Round 13, Z1(b) — third bucket. Missing on submissions evaluated
    // before this change; the backend normalises to [] on read.
    ideas: ErrorCluster[];
  };
  improvementSuggestions: string[];
  isHighestScore: boolean;
  // Round 18, FF2 — present only on a stem-completion task submission.
  stemCompletions?: string[];
  // Round 19, GG4 — one cluster from errorClusters above, chosen server-side
  // (base spec §3.4). Absent when there were no clusters to focus on.
  primaryFocus?: ErrorCluster;
  // Round 20, HH3 — coachFeedback/microFeedback have existed on the backend
  // since round 7 (base spec §9.1, a real p3-coach AI call on every
  // submission) but this type never declared them, so nothing ever rendered
  // them (see GG6's finding).
  // Round 28, QQ1(a) — coachFeedback ({criterion_view, social_feedback_view})
  // replaced by focusReason: a single learner-facing sentence on WHY the
  // Primary Focus matters, not what it is (criterion_view's 80-word
  // narrative repeated what the per-cluster feedback already said).
  // scaffoldHints removed entirely — its generating call (p3-scaffold) was
  // retired, not just unrendered; see CLAUDE.md's "computed and discarded"
  // standing check. Always present when the submission reached the coach
  // step at all.
  focusReason: string;
  microFeedback: string;
  // Round 132 (B) - present only when the attempt-4 (emergency_release) path generated them.
  modelAnswer?: string;
  recoveryFeedback?: string;
  /** Round 137 (decision 9): the writing pipeline judged this learner to be struggling -> a supportive message + "Contact support" */
  strugglingLearner?: boolean;
  /** Round 148 - always sent by the server; the phase is finished when passed, or on the last attempt (scaffoldPath 'emergency_release') */
  passed?: boolean;
  scaffoldPath?: 'standard' | 'scaffold_1' | 'scaffold_2' | 'emergency_release';
}

// Round 18, FF2 — discriminated by task shape: a stem-completion task
// sends stemCompletions, a plain-textarea task sends text. Never an
// assembled full passage — the backend re-resolves the stems itself and
// assembles server-side (a stale client copy of the stems could otherwise
// silently corrupt what gets evaluated).
export async function submitWritingResponse(
  sessionId: string,
  input: { text: string } | { stemCompletions: string[] },
): Promise<WritingEvaluation> {
  return apiFetch<WritingEvaluation>(`/sessions/${sessionId}/phase/writing/submit`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Speaking Phase ────────────────────────────────────────────────────────────

// Block S13, Part C — repointed from a route that was never registered
// (GET .../phase/speaking/context) to the real canonical phase-start route.
// startSpeakingPhase's response is wider than this (errorClusters too) —
// SpeakingStartContext keeps only what a call site actually consumes.
//
// Block S16 — vocabulary added. The backend's SpeakingPhaseStartResponse
// (session.types.ts) has carried {words, anchors} since it was written;
// this type simply never declared the field, so it arrived on every real
// response and was discarded before Phase4Speaking ever saw it — there were
// no vocabulary chips to fill because there was nothing to fill them from.
export interface SpeakingStartContext {
  alexContext: string | null;
  turnCount: number;
  scenarioTrigger: string | null;
  learnerRole: string | null;
  alexRole: string | null;
  challengeLevel: string | null;
  dualModeDefault: boolean;
  vocabulary: {
    words: string[];
    anchors: Record<string, {
      reading_sentences?: { id: string; text: string }[];
      listening_sections?: { id: string; text: string }[];
    }>;
  };
}

export async function getSpeakingContext(sessionId: string) {
  return apiFetch<SpeakingStartContext>(
    `/sessions/${sessionId}/phases/speaking/start`,
    { method: 'POST' },
  );
}

// Block S22 — post-session recap. Deliberately number-free (Nate's ruling):
// what to do differently next time, not how the learner scored.
export interface SpeakingRecap {
  wentWell: { learnerText: string; howItLanded: string } | null;
  toWorkOn: {
    patternFound:   boolean;
    description?:   string;
    exampleTurn?:   string;
    betterVersion?: string;
  } | null;
  vocabularyUsed:   string[];
  vocabularyUnused: string[];
}

export async function getSpeakingRecap(sessionId: string) {
  return apiFetch<SpeakingRecap>(`/sessions/${sessionId}/phase4/recap`, { method: 'GET' });
}

// ── Audio helpers ─────────────────────────────────────────────────────────────

// ── Sentence TTS ──────────────────────────────────────────────────────────────

/**
 * sentenceTTS — calls the sentenceTts Cloud Function, returns a playable audio URL.
 * Rate limited server-side: max 60 calls/user/day (shared with Phase 4 Alex TTS quota).
 */
export async function sentenceTTS(text: string, voiceName = 'aoede'): Promise<string> {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${previewPrefix()}/tts/sentence`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text, voiceName }),
  });
  void notifyIfRateLimited(res); // Round 108, C4
  if (!res.ok) throw new Error(`TTS request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || t('errors:apiProxy.ttsFailed'));

  // Decode base64 PCM → Blob URL
  const raw = atob(json.data.audioBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'audio/mp3' });
  return URL.createObjectURL(blob);
}

// ── Vocab Definition ──────────────────────────────────────────────────────────

export interface VocabDefinitionResult {
  word: string;
  definition: string;
  type: 'vocabulary' | 'grammar';
  cefrLevel: string;
  encounterCount: number;
  examples?: string[];
}

export async function getVocabDefinition(
  word: string,
  sessionId: string,
): Promise<VocabDefinitionResult> {
  // Round 129b (R-2): only the clicked word and the session are sent; the server derives the level and the context sentence from the lesson.
  return apiFetch<VocabDefinitionResult>('/vocab/definition', {
    method: 'POST',
    body: JSON.stringify({ word, sessionId }),
  });
}


// ── Speaking Phase (turn) ─────────────────────────────────────────────────────

// Block S13, Part C — the response now carries everything Phase4Speaking.tsx
// needs to thread into the NEXT turn's request (conversationHistory is built
// by the caller from its own turns list; everything else here has no other
// source, since the server holds none of it between calls except the
// composite EMA, which Part A already made server-authoritative and this
// component never sends back).
// Block S13, Part C — field names match p4.controller.ts's real response
// verbatim (`alexAudioBase64`, not `audioBase64`; `textPassComplete`, not
// `isComplete` — no such field was ever real; caught by reading the
// controller directly rather than trusting the dead route's old shape).
// Block S15/Group 2 — speechPassComplete added, symmetric to textPassComplete,
// wired to MIN_SPEECH_PASS_TURNS (previously zero consumers).
// Block S16 — microFeedback, scaffoldingContent, alexAudioMimeType added.
// All three were already sent by p4.controller.ts's response (verified
// directly, block S16-1) — this type simply never declared them, so they
// arrived on every real turn and were discarded: no per-turn Feedback text,
// no model-sentence/vocab-hints Scaffolding content, and playback always
// assumed 'audio/mp3' regardless of what TTS actually returned.
// Block S44, Stage 1 — alexAudioBase64/alexAudioMimeType/ttsAvailable removed:
// TTS no longer runs inside this request at all (p4Turn.service.ts). Audio is
// fetched separately, by alexTurnId, via fetchP4TurnAudioStream — see
// useStreamedAlexAudio.ts.
export interface SpeakingTurnResult {
  /** Round 153 B3 - what the app heard (the speech-to-text of a spoken turn); absent from an older backend */
  learnerTranscript?: string;
  alexText: string;
  alexTurnId: string;
  microFeedback: string;
  coachFeedback?: { howItLanded: string; strongerMove: string };
  scaffoldingContent: { modelSentence: string | null; vocabHints: string[] } | null;
  textPassComplete: boolean;
  speechPassComplete: boolean;
  /** Round 155d - Alex's reply is the closing turn (the server decided: every lesson-goal point covered at the floor, or the ceiling reached) */
  conversationClosed?: boolean;
  goalProgress?: { covered: number; total: number };
  alexEmotionalState: { rapport: number; patience: number; helpfulness: number };
  nextScaffoldingLevel: number;
  updatedAdaptation: {
    consecutiveStrongTurns: number;
    consecutiveWeakTurns: number;
    consecutiveRR0: number;
    moratoriumRemaining: number;
  };
  updatedCompositeScore: number | null;
  updatedTextPassComposite: number | null;
  composite: number;
}

export interface SpeakingTurnPersona {
  scenarioTrigger: string | null;
  learnerRole: string | null;
  alexRole: string | null;
  challengeLevel: string | null;
}

export interface SpeakingTurnState {
  conversationHistory: Array<{ sender: string; text: string }>;
  precedingAlexText: string;
  alexEmotionalState?: { rapport: number; patience: number; helpfulness: number };
  scaffoldingLevel: number;
  scaffoldingAdaptation?: {
    consecutiveStrongTurns: number;
    consecutiveWeakTurns: number;
    consecutiveRR0: number;
    moratoriumRemaining: number;
  };
  speechPassTurnCount: number;
  textPassTurnCount: number;
  recentTurnComposites: number[];
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// Block S13, Part C — repointed from a dead multipart route
// (POST .../phase/speaking/turn) to the real /phase4/turn. Reconciliation
// chosen on the frontend side (base64-encode the recording) rather than the
// backend (accept multipart) — every real test of this endpoint all
// engagement has used JSON+audioBase64, and adding multipart parsing would
// introduce middleware nothing else in this JSON-only API uses, for one
// route. Block 0's session validation (404/403/409) and cefrLevel derivation
// are untouched — this only changes how the request reaches that code, not
// what it does. compositeScore/textPassComposite are deliberately NOT sent
// (Part A made the server the sole source; sending them back would
// reintroduce the exact client-spoofable vector that fix closed).
export async function submitSpeakingTurn(
  sessionId: string,
  passMode: 'text_pass' | 'speech_pass',
  input: { audioBlob: Blob; mimeType: string } | { textInput: string },
): Promise<SpeakingTurnResult> {
  // Round 129 - ONLY the learner's own input is sent. The persona, the history, the turn counts, the recent composites, Alex's mood and
  // the scaffolding level/adaptation are all held by the server (it persisted every turn); sending them is a 400 FORBIDDEN_CLIENT_FIELD.
  const body: Record<string, unknown> = { passMode };
  if ('audioBlob' in input) {
    body.audioBase64 = await blobToBase64(input.audioBlob);
    body.mimeType = input.mimeType;
  } else {
    body.textInput = input.textInput;
  }

  return apiFetch<SpeakingTurnResult>(`/sessions/${sessionId}/phase4/turn`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Block S44, Stage 2 — the raw streamed-audio fetch used by
// useStreamedAlexAudio.ts. Deliberately NOT routed through apiFetch: that
// helper always calls `res.json()` on the body, which would consume (and
// choke on) a binary PCM stream. Auth follows the exact same pattern as
// every other call in this file (Firebase ID token, Authorization header) —
// this endpoint uses bearer-token auth like the rest of the API, not cookies.
export async function fetchP4TurnAudioStream(sessionId: string, alexTurnId: string): Promise<Response> {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${previewPrefix()}/sessions/${sessionId}/phase4/turn/${alexTurnId}/audio`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  void notifyIfRateLimited(res); // Round 108, C4 — inspects a clone; the caller's stream is untouched
  return res;
}

// ── Session checkpoint ────────────────────────────────────────────────────────

export async function reportAudioFailed(sessionId: string) {
  return apiFetch<void>(`/sessions/${sessionId}/phase/listening/audio-failed`, { method: 'POST' });
}

export async function skipSpeakingPhase(
  sessionId: string,
  reason: 'mic_permission_denied' | 'mic_not_found' | 'silence_timeout' | 'text_fallback',
) {
  return apiFetch<void>(`/sessions/${sessionId}/phase/speaking/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ── Account Management ────────────────────────────────────────────────────────

/** Round 136: records a deletion REQUEST; the answer carries the date after which the data is deleted for good (6-month grace period). */
export async function deleteAccount(confirmEmail: string): Promise<{ purgeAfter: string }> {
  return apiFetch<{ purgeAfter: string }>('/auth/account', {
    method: 'DELETE',
    body: JSON.stringify({ confirmEmail }),
  });
}

/** Round 139 - the signed-in learner's own deletion state (allowed while a deletion is pending). */
export async function getMyDeletionStatus(): Promise<{ status: 'none' | 'pending' | 'purging' | 'purged' | 'cancelled'; purgeAfter: string | null; canCancel: boolean }> {
  return apiFetch('/auth/account/deletion');
}

/** Round 139 - "Keep my account": cancels the learner's OWN deletion request (no body; the server decides whether it may). */
export async function keepMyAccount(): Promise<{ status: 'cancelled' }> {
  return apiFetch('/auth/account/deletion/cancel', { method: 'POST' });
}

// ── Profile Analytics ─────────────────────────────────────────────────────────

export interface LessonHistoryItem {
  sessionId: string;
  lessonId: string;
  lessonTitle: string;
  cefrLevel: string;
  completedAt: string;
  overallScore: number;
  phaseScores: {
    reading: number;
    listening: number;
    writing: number;
    speaking: number;
  };
  durationMs: number;
  vocabularyEncountered: number;
}

export async function getLessonHistory(
  page: number = 1,
  limit: number = 10
): Promise<{ sessions: LessonHistoryItem[]; total: number; page: number; limit: number }> {
  return apiFetch<{ sessions: LessonHistoryItem[]; total: number; page: number; limit: number }>(
    `/profile/lesson-history?page=${page}&limit=${limit}`
  );
}

export interface VocabularyStatsData {
  totalWords: number;
  masteredWords: number;
  inProgressWords: number;
  byLevel: Record<string, number>;
  byEncounterType: {
    heard: number;
    read: number;
    written: number;
    spoken: number;
  };
  recentWords: Array<{
    word: string;
    consolidationScore: number;
    cefrLevel: string;
    lastEncounteredAt?: string;
  }>;
}

export async function getVocabularyStats(): Promise<VocabularyStatsData> {
  return apiFetch<VocabularyStatsData>('/profile/vocabulary-stats');
}

export interface ProgressDataPoint {
  completedAt: string;
  overallScore: number;
  phaseScores: {
    reading: number;
    listening: number;
    writing: number;
    speaking: number;
  };
  cefrLevel: string;
}

export async function getProgressOverTime(): Promise<{ dataPoints: ProgressDataPoint[] }> {
  return apiFetch<{ dataPoints: ProgressDataPoint[] }>('/profile/progress-over-time');
}

// Round 27 — the learner-facing block-progress signal. Mirrors
// src/services/progressionProgressSignal.service.ts's BlockProgressSignal/
// BlockProgressComparison exactly; every number here is read straight off
// that response, never recomputed client-side (see the round's own C2
// reasoning: recomputing any of this here would reintroduce the
// two-implementations-of-one-rule risk the whole round exists to close).
export type PhaseKey = 'reading' | 'listening' | 'writing' | 'speaking';

export interface BlockProgressComparison {
  windowBaseIMean: number;
  // Round 127 - the gate counts lessons: qualifyingCount of windowSize lessons scored >= threshold; requiredShare of them are needed.
  qualifyingCount: number;
  windowSize: number;
  requiredShare: number;
  threshold: number;
  aggregateMeetsThreshold: boolean;
  perSkillBaseMean: Partial<Record<PhaseKey, number>>;
  baseSkillFloor: number;
  namedSkill: PhaseKey | null;
}

export interface BlockProgressSignal {
  lessonsDone: number;
  lessonsTotal: number;
  comparisonUnlocked: boolean;
  // Round 29, C — whether LessonCompletionPage should show this as a
  // popup this lesson (always true on any floor breach; also true at the
  // fixed milestones 10/30/60/90/110). Computed server-side, same source
  // as `comparison` — never re-derived client-side.
  showAsPopup: boolean;
  comparison: BlockProgressComparison | null;
}

export async function getBlockProgress(): Promise<BlockProgressSignal> {
  return apiFetch<BlockProgressSignal>('/profile/block-progress');
}

// ── Admin: Users ──────────────────────────────────────────────────────────────

export interface AdminUser {
  uid: string;
  email: string;
  preferredName?: string;
  firstName?: string;
  lastName?: string;
  subjectArea?: string;
  placementLevel?: string;
  currentLevel?: Record<string, string>;
  accessStatus: string;
  lessonsCompleted: number;
  currentStreak: number;
  lastActiveAt?: string;
  subscription: { status: string; planId?: string; gateway?: string };
  /** Round 141 - how the account signs in (from Firebase Auth) and whether its e-mail address is confirmed (null = unknown) */
  signInMethod?: 'google' | 'email' | 'both' | 'unknown';
  emailVerified?: boolean | null;
  // Round 53, B6 — the badge AdminUsers.tsx renders without a second fetch.
  progressReview?: {
    status: 'flagged' | 'resolved';
    flaggedAt: string;
    flaggedSkills: string[];
    flaggedAtBlock: string;
    resolvedAt?: string;
    resolvedBy?: string;
    resolutionNote?: string;
  } | null;
}

// Round 114b — lesson metadata for the admin Content library and dashboard tile. These were direct browser reads of
// lessonCatalog / lessonSpecs; firestore.rules now refuses every client read of lesson content, so they go via the API.
export interface AdminLessonRow {
  id: string; lessonId: string; title: string | null; cefrLevel: string | null; fieldId: string | null;
  lessonIndex: number; topic: string | null; published: boolean; readyForLearners: boolean; archived?: boolean;
}
/** archived lessons (Round 116b) are left out unless asked for */
export async function getAdminLessonLibrary(includeArchived = false): Promise<AdminLessonRow[]> {
  return (await apiFetch<{ lessons: AdminLessonRow[] }>(`/admin/lesson-library${includeArchived ? '?includeArchived=1' : ''}`)).lessons;
}
export async function getAdminLessonStats(): Promise<{ total: number; live: number }> {
  return apiFetch<{ total: number; live: number }>('/admin/lesson-stats');
}

export async function getAdminUsers(filters: {
  search?: string;
  status?: string;
  level?: string;
  flagged?: boolean;
  page?: number;
  limit?: number;
} = {}): Promise<{ users: AdminUser[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.level) params.append('level', filters.level);
  if (filters.flagged) params.append('flagged', 'true');
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  return apiFetch<{ users: AdminUser[]; total: number; page: number; limit: number }>(
    `/admin/users?${params.toString()}`
  );
}

export async function getAdminUserDetail(uid: string): Promise<any> {
  return apiFetch<any>(`/admin/users/${uid}`);
}

export async function suspendUser(uid: string): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>(`/admin/users/${uid}/suspend`, {
    method: 'POST',
  });
}

export async function unsuspendUser(uid: string): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>(`/admin/users/${uid}/unsuspend`, {
    method: 'POST',
  });
}

// Round 58, B2 — a full re-placement: locks all four CEFR skills again
// (placementLocked), abandons any dangling in-progress placement attempt,
// and unlocks trial lessons for the new level — the only route past
// placementTestService.startTest's one-shot gate.
export async function resetUserPlacement(
  uid: string,
  cefrLevel: 'A1' | 'A2' | 'B1' | 'B2' | 'C1',
  reason?: string
): Promise<{ success: boolean; message: string; data: { userId: string; cefrLevel: string; unlockedLessonIds: string[] } }> {
  return apiFetch(`/admin/placement/${uid}/reset`, {
    method: 'POST',
    body: JSON.stringify({ cefrLevel, reason }),
  });
}

// Round 53, B6 — clears the account-level human-review flag
// (progressReview.status: 'flagged' -> 'resolved'), restoring lesson access
// and resubscribe eligibility in one admin action.
export async function clearProgressReviewFlag(
  uid: string,
  note?: string
): Promise<{ success: boolean; message: string; data: { uid: string; resolvedAt: string; resolvedBy: string } }> {
  return apiFetch(`/admin/users/${uid}/clear-progress-review`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

// Round 57, A3 — support and above (unlike clearProgressReviewFlag above,
// which stays super_admin-only per the owner's C4 ruling).
export async function escalateProgressReview(
  uid: string,
  note?: string
): Promise<{ success: boolean; data: { uid: string; status: string; escalatedAt: string } }> {
  return apiFetch(`/admin/users/${uid}/progress-review/escalate`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export async function addProgressReviewNote(
  uid: string,
  text: string
): Promise<{ success: boolean; data: { uid: string; note: { authorId: string; authorTier: string; text: string; at: string } } }> {
  return apiFetch(`/admin/users/${uid}/progress-review/note`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// ── Admin: Problem Reports (Round 57, C1) ───────────────────────────────────────

export interface AdminProblemReportSummary {
  reportId: string;
  createdAt: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  description: string;
  url: string;
  status: 'open' | 'in_progress' | 'resolved';
  handledBy: string | null;
  attachmentCount: number;
  /** Round 143: 'website' = a message from the irtaki.net contact form (no account; userId is null) */
  kind?: 'report' | 'website';
  topic?: 'general' | 'course' | 'payments' | 'partnership' | 'other' | null;
  lang?: 'en' | 'ar' | null;
}

export interface AdminProblemReportDetail extends AdminProblemReportSummary {
  screenshotPath: string | null;
  attachmentPaths: string[];
  consoleLog: { level: string; message: string; timestamp: string }[];
  handledAt: string | null;
  resolutionNote: string | null;
  screenshotUrl: string | null;
  attachmentUrls: (string | null)[];
  refusedAttachments?: Array<{ name: string; reason: 'type' | 'size' | 'count' | 'upload_failed' }>;
  /** Round 120 - the conversation after the report itself */
  thread?: Array<{ id: string; from: 'learner' | 'support'; body: string; at: string; authorUid: string | null; emailStatus?: 'sent' | 'not_sent' | null; emailReason?: string | null; staffNotified?: { status: 'sent' | 'not_sent'; reason: string | null } | null }>;
  /** Round 130 (R130-10): was the team emailed about the learner's ORIGINAL report (the replies carry their own outcome) */
  staffNotified?: { status: 'sent' | 'not_sent'; reason: string | null; at: string | null } | null;
}

export async function getAdminProblemReports(filters: {
  status?: string;
  page?: number;
  limit?: number;
  /** Round 143 */
  kind?: 'report' | 'website';
  topic?: string;
} = {}): Promise<{ reports: AdminProblemReportSummary[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.kind) params.append('kind', filters.kind);
  if (filters.topic) params.append('topic', filters.topic);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));
  return apiFetch(`/admin/problem-reports?${params.toString()}`);
}

/** Round 143 - deletes ONE website message and its replies (admin and above; a learner's report is refused). */
export async function deleteWebsiteMessage(reportId: string): Promise<{ deleted: true; replies: number }> {
  return apiFetch(`/admin/problem-reports/${encodeURIComponent(reportId)}`, { method: 'DELETE' });
}

// ── Round 130: answer timing (R130-6) and the owner's email switches (R130-3), both super_admin ──────────────────────
export interface TimedAnswerRow { kind: 'speaking_turn' | 'writing_submission'; at: string | null; seconds: number | null; chars: number | null; words: number | null; mode: string | null; flag: string | null }
export interface PhaseTimingRow { phase: 'reading' | 'listening' | 'writing' | 'speaking'; servedAt: string | null; answeredAt: string | null; seconds: number | null; answers: number | null; flag: string | null; items: TimedAnswerRow[] }
export interface SessionTimingRow { sessionId: string; lessonId: string; startedAt: string | null; status: string | null; phases: PhaseTimingRow[]; flagged: boolean }
export interface AnswerTimingReport { sessions: SessionTimingRow[]; thresholds: Record<string, number> }
// Round 147b: answers whose text addressed the AI grader (staff only; the learner is never told)
export interface GraderFlagView { signals: string[]; capped: boolean; removed: string[] }
export interface LessonGraderFlagRow extends GraderFlagView { sessionId: string; lessonId: string; phase: 'writing' | 'speaking'; at: string | null }
export interface PlacementAttemptFlagsRow { attemptId: string; skill: string; status: string | null; startedAt: string | null; completedAt: string | null; finalLevel: string | null; answers: Array<GraderFlagView & { itemId: string; at: string | null; verdict: boolean | null }> }
export const getGraderFlags = (uid: string) => apiFetch<{ lessons: LessonGraderFlagRow[]; placement: PlacementAttemptFlagsRow[] }>(`/admin/users/${encodeURIComponent(uid)}/grader-flags`);
export const getAnswerTiming = (uid: string, limit = 10) => apiFetch<AnswerTimingReport>(`/admin/users/${encodeURIComponent(uid)}/answer-timing?limit=${limit}`);
export interface EmailSwitches { switches: Record<string, boolean>; defaults: Record<string, boolean> }
export const getEmailSwitches = () => apiFetch<EmailSwitches>('/admin/email-notifications');
export const setEmailSwitch = (type: string, enabled: boolean) => apiFetch<EmailSwitches>('/admin/email-notifications', { method: 'PUT', body: JSON.stringify({ type, enabled }) });

// ── Round 144: Data retention (support and above read; the super admin switches, sets the threshold, starts a practice run) ──
export type RetentionSwitchName = 'unconfirmedAccounts' | 'inactiveAccounts' | 'websiteMessages';
export const RETENTION_SWITCH_NAMES: RetentionSwitchName[] = ['unconfirmedAccounts', 'inactiveAccounts', 'websiteMessages'];
export interface RetentionList { wouldRemind: number; reminded: number; wouldDelete: number; deleted: number; remindIds: string[]; deleteIds: string[] }
export interface RetentionReportView {
  id: string | null; kind: 'nightly' | 'practice'; at: string; acted: boolean; scanned: number; errors: number;
  switches: Record<RetentionSwitchName, boolean> | null; lists: Record<RetentionSwitchName, RetentionList>;
}
export interface RetentionPerson { id: string; email: string | null; exists: boolean; topic?: string | null }
export interface RetentionReportDetail extends RetentionReportView { people: Record<RetentionSwitchName, { remind: RetentionPerson[]; delete: RetentionPerson[] }> }
export interface RetentionPage {
  switches: Record<RetentionSwitchName, boolean>; wouldDeleteAlertThreshold: number; legacyEnabled: boolean;
  info: Record<RetentionSwitchName, { label: string; does: string; confirmPhrase: string }>;
  reports: RetentionReportView[]; practiceRun: { lastAt: string | null; nextAllowedAt: string | null };
  /** Round 147: the shared daily e-mail budget (today's figures + the settings) */
  emailBudget?: EmailBudgetStatus;
}
export const getRetention = () => apiFetch<RetentionPage>('/admin/retention');
export const getRetentionReportDetail = (id: string) => apiFetch<RetentionReportDetail>(`/admin/retention/reports/${encodeURIComponent(id)}`);
export const setRetentionSwitch = (name: RetentionSwitchName, enabled: boolean, confirm?: string) =>
  apiFetch<{ switches: Record<RetentionSwitchName, boolean>; wouldDeleteAlertThreshold: number }>('/admin/retention/switch', { method: 'PUT', body: JSON.stringify(enabled ? { name, enabled, confirm } : { name, enabled }) });
export const setRetentionThreshold = (value: number) =>
  apiFetch<{ switches: Record<RetentionSwitchName, boolean>; wouldDeleteAlertThreshold: number }>('/admin/retention/threshold', { method: 'PUT', body: JSON.stringify({ value }) });
// Round 147 (A2): the shared daily e-mail budget - support+ read it with the page, the super admin sets the three numbers (audit row)
export interface EmailBudgetConfig { providerDailyLimit: number; publicDailyCap: number; priorityReserve: number }
export interface EmailBudgetStatus {
  day: string; config: EmailBudgetConfig; percent: number; warnAt: number;
  counts: { public: number; standard: number; priority: number; total: number; warnedAt: string | null };
}
export const setEmailBudget = (c: EmailBudgetConfig) => apiFetch<{ emailBudget: EmailBudgetStatus }>('/admin/retention/email-budget', { method: 'PUT', body: JSON.stringify(c) });
export const runRetentionPracticeRun = () => apiFetch<RetentionReportView>('/admin/retention/practice-run', { method: 'POST', body: JSON.stringify({}) });

// ── Round 144: Scheduled jobs (support and above, read-only) ──
export interface JobRunView { startedAt: string; finishedAt: string; durationMs: number; outcome: 'ok' | 'failed' | 'skipped'; summary: Record<string, number>; error: string | null }
export interface ScheduledJobView {
  jobName: string; cron: string; cadence: 'hourly' | 'daily' | 'weekly'; what: string; schedule: { utc: string; paris: string }; missAfterHours: number;
  last: JobRunView | null; missed: boolean; overdueByMs: number; history: JobRunView[]; failedInHistory: number;
}
export const getScheduledJobs = () => apiFetch<{ now: string; trackingSince: string; jobs: ScheduledJobView[] }>('/admin/scheduled-jobs');
export interface SystemAlertView {
  key: string; kind: 'job_failed' | 'job_missed' | 'inactivity_early' | 'retention_spike' | 'email_budget' | 'unservable_content'; title: string; detail: string; page: 'jobs' | 'retention' | 'content';
  raisedAt: string; lastSeenAt: string; count: number; email: { status: 'sent' | 'not_sent'; reason: string | null };
}
export const getSystemAlerts = (page: 'jobs' | 'retention' | 'content') => apiFetch<{ alerts: SystemAlertView[] }>(`/admin/system-alerts?page=${page}`);
// ── Round 149: Lessons with unservable activities (support and above, read-only; the last daily content scan) ──
export interface ContentScanView {
  scannedAt: string;
  counts: { lessons: number; withUnservable: number; blocked: number; practiceOnly: number; live: number; reServedActivities: number };
  lessons: { lessonId: string; field: string | null; live: boolean; archived: boolean; blocked: boolean; practiceOnlyPhases: ('reading' | 'listening')[]; unservableCount: number; messages: string[] }[];
  truncated: boolean;
}
export const getUnservableLessons = () => apiFetch<{ scan: ContentScanView | null }>('/admin/content/unservable');
export const acknowledgeSystemAlert = (key: string) => apiFetch<{ alert: SystemAlertView }>(`/admin/system-alerts/${encodeURIComponent(key)}/acknowledge`, { method: 'POST', body: JSON.stringify({}) });

// ── Admin: Analytics (Round 140, super_admin; anonymous roll-ups, no group under 5 learners) ──────────────────────────────────────────
export type AnalyticsGranularity = 'day' | 'week' | 'month';
export interface AnalyticsPeriodRow { key: string; from: string; to: string; computedAt: string; capped: boolean; completeFrom: string | null }
export interface AnalyticsQuery { period: string; includeTest?: boolean; startLevel?: string; tenure?: string; country?: string; lang?: string; specialty?: string; plan?: string }
export interface AnalyticsAiTotals { calls: number; fails: number; failRate: number | null; avgLatencyMs: number | null; fallbacks: number; chars: number; usd: number | null; unpricedCalls: number }
export interface AnalyticsViewShown {
  suppressed: false;
  overview: { learners: number; newLearners: number; newAccounts: number | null; lessonsStarted: number; lessonsCompleted: number; retakes: number; completionRate: number | null;
    lessonsPerLearner: number | null; abandonedByPart: Record<string, number>; retention: { prevActive: number; retained: number; rate: number | null } | null; gate: Record<string, number> };
  time: { avgLessonMinutes: number | null; durationHist: { label: string; lessons: number }[]; heat: { slot: string; lessons: number; learners: number }[]; heatHidden: number };
  performance: { skill: string; learners: number; avgScore: number | null; avgMinutes: number | null; hist: { label: string; n: number }[] | null }[];
  placement: { skill: string; completed: number; avgItems: number | null; avgMinutes: number | null; levels: Record<string, number>; stops: Record<string, number> }[];
  placementHidden: number;
  mistakes: { writing: { cluster: string; level: string; drafts: number; learners: number }[]; writingHidden: number; writingAvg: number | null;
    speaking: { turns: number; speech: number; text: number; dims: Record<string, number | null> } | null;
    activities: { phase: string; type: string; answers: number; correctRate: number | null; avgTries: number | null; learners: number }[]; activitiesHidden: number };
  ai: { rows: (AnalyticsAiTotals & { task: string; provider: string; model: string; learners: number; tokensIn: number; tokensOut: number })[]; hiddenRows: number; totals: AnalyticsAiTotals;
    usdPerLearner: number | null; usdPerLesson: number | null; anonymous: AnalyticsAiTotals | null };
  breakdowns: Record<string, { rows: { value: string; learners: number; lessonsCompleted: number }[]; hiddenLearners: number }>;
}
export type AnalyticsView = AnalyticsViewShown | { suppressed: true; learners: number | null; minGroup: number };
export interface AnalyticsViewResponse {
  period: { key: string; granularity: AnalyticsGranularity; from: string; to: string; computedAt: string; capped: boolean; completeFrom: string | null };
  filters: AnalyticsQuery; minGroup: number; view: AnalyticsView;
  trend: { key: string; learners: number | null; lessonsCompleted: number | null }[];
  options: Record<string, string[]>; priceWarnings: string[];
}
export interface AnalyticsPriceRow { provider: string; model: string; unit: 'tokens' | 'characters'; inputText?: number | null; inputAudio?: number | null; cachedInput?: number | null; output?: number | null;
  perMillionChars?: number | null; sourceUrl: string; readOn: string; validUntil?: string | null; note?: string | null }
export interface AnalyticsPriceTable { rows: AnalyticsPriceRow[]; updatedAt: string | null; updatedBy: string | null; isDefault: boolean }

const analyticsParams = (q: AnalyticsQuery) => {
  const p = new URLSearchParams({ period: q.period });
  if (q.includeTest) p.set('includeTest', '1');
  for (const k of ['startLevel', 'tenure', 'country', 'lang', 'specialty', 'plan'] as const) if (q[k]) p.set(k, q[k] as string);
  return p;
};
export const getAnalyticsPeriods = (granularity: AnalyticsGranularity) =>
  apiFetch<{ granularity: AnalyticsGranularity; periods: AnalyticsPeriodRow[]; earliest: string | null }>(`/admin/analytics/periods?granularity=${granularity}`);
export const getAnalyticsView = (q: AnalyticsQuery) => apiFetch<AnalyticsViewResponse>(`/admin/analytics/view?${analyticsParams(q).toString()}`);
export const getAnalyticsPrices = () => apiFetch<AnalyticsPriceTable>('/admin/analytics/prices');
export const saveAnalyticsPrice = (row: AnalyticsPriceRow) => apiFetch<AnalyticsPriceTable>('/admin/analytics/prices', { method: 'PUT', body: JSON.stringify(row) });
export const getAnalyticsTestAccounts = () =>
  apiFetch<{ accounts: { uid: string; name: string | null; email: string | null }[]; staffAccounts: number; rules: string[] }>('/admin/analytics/test-accounts');
export const changeAnalyticsTestAccount = (uid: string, action: 'add' | 'remove') =>
  apiFetch<{ count: number; note: string }>('/admin/analytics/test-accounts', { method: 'POST', body: JSON.stringify({ uid, action }) });
export const recomputeAnalyticsPeriod = (period: string) =>
  apiFetch<{ key: string; computedAt: string; cells: number }>('/admin/analytics/recompute', { method: 'POST', body: JSON.stringify({ period }) });
/** The selection's tables as a CSV or Excel file (same figures as the screen; audited on the server). */
export async function downloadAnalytics(q: AnalyticsQuery, format: 'csv' | 'xlsx'): Promise<{ blob: Blob; fileName: string }> {
  const params = analyticsParams(q); params.set('format', format);
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}/admin/analytics/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let message = `HTTP ${res.status}`; let code: string | undefined;
    try { const j = await res.json(); message = j.error || message; code = j.code; if (code === 'MFA_REQUIRED' && typeof window !== 'undefined') window.dispatchEvent(new Event(ADMIN_MFA_REQUIRED_EVENT)); } catch { /* not JSON */ }
    throw new ApiFetchError(message, res.status, code);
  }
  const fileName = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `analytics.${format}`;
  return { blob: await res.blob(), fileName };
}

export async function getAdminProblemReportDetail(reportId: string): Promise<AdminProblemReportDetail> {
  return apiFetch<AdminProblemReportDetail>(`/admin/problem-reports/${reportId}`);
}

export async function updateProblemReportStatus(
  reportId: string,
  status: 'open' | 'in_progress' | 'resolved'
): Promise<{ reportId: string; status: string }> {
  return apiFetch(`/admin/problem-reports/${reportId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function replyToProblemReport(reportId: string, body: string): Promise<{ message: { id: string; from: 'support'; body: string; at: string }; emailed: boolean; email?: { status: 'sent' | 'not_sent'; reason: string | null } }> {
  return apiFetch(`/admin/problem-reports/${encodeURIComponent(reportId)}/reply`, { method: 'POST', body: JSON.stringify({ body }) });
}

// ── Admin: Fields (specialties) ───────────────────────────────────────────────

export interface Field {
  fieldId: string;
  slug: string;
  displayName: string;
  totalLessons: number;
  subLevelCounts: Record<string, number>;
}

export async function createField(displayName: string): Promise<{ success: boolean; data: Field }> {
  return apiFetch<{ success: boolean; data: Field }>('/admin/fields', {
    method: 'POST',
    body: JSON.stringify({ displayName }),
  });
}

// ── Admin: lesson detail (Round 115) ──────────────────────────────────────────
// The old ZIP uploader's client calls were removed in Round 115 (replaced by the folder loader page, which has its own
// API client beside it). Its backend endpoints are still deployed; see CLAUDE.md "Round 115".
export interface AdminLessonDetailHistory {
  jobId: string; createdAt: string; createdBy: string | null; createdByEmail: string | null; jobStatus: string;
  result: 'committed' | 'failed' | null; error: string | null; files: number | null; sourceBytes: number | null; finalBytes: number | null; transcodedFiles: number | null; ms: number | null;
}
export interface AdminLessonDetail {
  lesson: {
    lessonId: string; fieldId: string; title: string; topic: string; cefrLevel: string; variation: string; lessonIndex: number;
    published: boolean; archived: boolean; archivedAt: string | null; uploadedAt: string | null; publishedAt: string | null; publishedBy: string | null; unpublishedAt: string | null; unpublishedBy: string | null; ingestKey: string | null;
  };
  media: { complete: boolean; problems: string[]; pathsChecked: number };
  variants: { reading: Array<{ level: string; sentences: number }>; listening: Array<{ level: string }>; enrichedServedLevels: string[] };
  assets: { illustrations: { reading: string | null; listening: string | null }; dialogue: Record<string, string | null>; sentences: Record<string, Array<string | null>> };
  ingest: { fingerprint: string | null; ingestedAt: string | null; jobId: string | null } | null;
  history: AdminLessonDetailHistory[];
}
// ── Admin: "Preview as learner" (Round 116) ───────────────────────────────────────────────────────────────────
export interface AdminPreviewRun { previewId: string; lessonId: string; fieldId: string; level: string; ladder: string[]; title: string; expiresAt: string }
/** admin and above. Starts one preview run (a sandbox learner owned by the caller); the player then uses /preview/:previewId/*. */
export async function prepareAdminPreview(lessonId: string, level?: string): Promise<AdminPreviewRun> {
  return apiFetch<AdminPreviewRun>('/admin/preview/prepare', { method: 'POST', body: JSON.stringify({ lessonId, ...(level ? { level } : {}) }) });
}

// ── Admin: read-only lesson verification (Round 116) ──────────────────────────────────────────────────────────
export type VerifyCheckLevel = 'pass' | 'fail' | 'warn' | 'info';
export interface VerifyCheckRow { name: string; level: VerifyCheckLevel; detail: string }
export interface LessonVerificationRow {
  lessonId: string; field: string | null; published: boolean | null; ok: boolean;
  counts: { mediaFiles: number; signedUrls: number; probed: number; failures: number; warnings: number };
  checks: VerifyCheckRow[]; ladder: string[]; durations: Array<{ path: string; durationSec: number | null }>;
}
export interface LessonVerificationReport {
  generatedAt: string; readOnly: true; options: { probe: string; fetchUrls: string };
  summary: { lessons: number; ok: number; failed: number }; lessons: LessonVerificationRow[];
}
/** super_admin; at most 6 lesson ids per call - the dashboard sends the library a few at a time. Never writes. */
export async function verifyAdminLessons(lessonIds: string[]): Promise<LessonVerificationReport> {
  return apiFetch<LessonVerificationReport>('/admin/lesson-verify', { method: 'POST', body: JSON.stringify({ lessonIds }) });
}

export async function getAdminLessonDetail(lessonId: string): Promise<AdminLessonDetail> {
  return apiFetch<AdminLessonDetail>(`/admin/lesson-detail/${encodeURIComponent(lessonId)}`);
}

// ── Payments (learner-facing checkout) ────────────────────────────────────────
// Part A1 — real wiring for src/routes/payments.routes.ts. Previously these
// backend endpoints had zero live frontend callers at all (SubscribePage.tsx
// redirected to a static buy.stripe.com Payment Link instead) — see that
// round's own commit for the trace. These three functions are the real
// callers now.

export type ResolvedCheckoutCurrency = 'usd' | 'dzd';
export type CheckoutPlanId = 'monthly' | 'block';

export interface ResolvedPlanPrice {
  /** Native gateway unit: USD cents (Stripe) or whole DZD (SlickPay). null when not configured for this currency. */
  amount: number | null;
  available: boolean;
  /** Part C1/D — admin-authored name/period override, English-only. Absent = use the catalog-driven (translated) default. */
  copy?: { name: string; period: string };
  /** Round 79, Part A — admin-selected, ordered feature-bullet identifiers. Absent = use DEFAULT_PLAN_FEATURE_BULLETS. Resolved to translated text via constants/featureBullets.ts, never sent as text. */
  featureBulletIds?: string[];
}

export interface ResolvedPlanPrices {
  currency: ResolvedCheckoutCurrency;
  country: string | null;
  plans: Record<CheckoutPlanId, ResolvedPlanPrice>;
  /**
   * Round 117 - card (Stripe) checkout is on hold. Only an explicit `true` shows card plans; anything else
   * (false, absent, an older backend) shows "Card payment coming soon" to USD learners.
   */
  cardPaymentsEnabled?: boolean;
  /** Round 117 - present ONLY for super_admin accounts: the hidden SlickPay TEST product (whole DZD, days of access). */
  testOffer?: { amount: number; accessDays: number; currency: 'dzd' };
  /** Round 128b - covered = an active block plan still covers the attempt in progress: the block cannot be bought until lesson 120 is finished. */
  blockPurchase?: { covered: boolean; level?: string; lessonsDone?: number; total?: number; expiresAt?: string | null };
}

/** SlickPay checkout also accepts the hidden 'test' product (super_admin only; the server enforces it). */
export type SlickPayCheckoutPlanId = CheckoutPlanId | 'test';

export async function getPaymentPrices(): Promise<ResolvedPlanPrices> {
  return apiFetch<ResolvedPlanPrices>('/payments/prices');
}

export async function createStripeCheckout(payload: {
  planType: CheckoutPlanId;
  geo?: string;
  /** Round 132b - the learner ticked "I have read and agree to the Terms of Use and the Privacy Policy" (the server refuses a checkout without it) */
  acceptedTerms?: boolean;
}): Promise<{ url: string; mode: 'subscription' | 'payment' }> {
  return apiFetch('/payments/stripe/checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createSlickPayCheckout(payload: {
  planType: SlickPayCheckoutPlanId;
  /** Round 132b - see createStripeCheckout (the owner's TEST product is exempt on the server) */
  acceptedTerms?: boolean;
  // Round 94, B4 — only supplied when the learner's own profile has no
  // stored phone (an account created before this round); normally omitted,
  // since checkout reads it from the profile server-side.
  phone?: string;
}): Promise<{ invoiceId: number; paymentUrl: string; ref?: string }> {
  return apiFetch('/payments/slickpay/checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Where a SlickPay invoice is in our books (backend payment.service.ts SlickPayState). */
export type SlickPayPaymentState =
  | 'pending' | 'processing' | 'resolved' | 'failed' | 'cancelled' | 'expired' | 'mismatch' | 'activation_blocked' | 'unknown';

export interface SlickPayConfirmResult {
  /** true only when the plan is activated */
  resolved: boolean;
  state: SlickPayPaymentState;
  invoiceId: string;
  paymentStatus?: string;
  /** SlickPay could not be reached this time; `state` is the last known one */
  checkFailed?: boolean;
  isTest?: boolean;
  amountDZD?: number;
  planType?: string;
  ref?: string;
}

/**
 * Round 94, A2/E - the confirm call PaymentSuccessPage.tsx makes on return from SlickPay's payment page,
 * and again from its "check payment status again" button (`manual`). Round 117: the invoice can be named by
 * `invoiceId`, by our `ref` (carried in the return URL) or not at all (the server then picks the caller's
 * most recent unsettled invoice).
 */
export async function confirmSlickPayPayment(params: { invoiceId?: number | string; ref?: string; manual?: boolean } = {}): Promise<SlickPayConfirmResult> {
  return apiFetch('/payments/slickpay/confirm', {
    method: 'POST',
    body: JSON.stringify({
      ...(params.invoiceId !== undefined ? { invoiceId: params.invoiceId } : {}),
      ...(params.ref ? { ref: params.ref } : {}),
      ...(params.manual ? { source: 'manual' } : {}),
    }),
  });
}

// ── Admin: Payments ───────────────────────────────────────────────────────────

export type PaymentGroup = 'paid' | 'failed' | 'pending' | 'refunded' | 'refused' | 'error';
export interface AdminPayment {
  id: string;
  userId: string;
  userEmail: string;
  gateway: string;
  amountPaid: number;
  currency: string;
  eventType: string;
  /** Round 120 - paid | failed | pending | refunded */
  group: PaymentGroup;
  timestamp: string;
  gatewayRef: string;
  planId: string;
  country: string;
  specialty: string;
  /** Round 117 - the hidden super_admin SlickPay TEST product. */
  isTest?: boolean;
  /** Round 122 - set on checkout-attempt rows (group refused | error) */
  kind?: 'payment' | 'attempt';
  userName?: string;
  userPhone?: string;
  gatewayMessage?: string;
  initiatedBy?: 'learner' | 'admin';
  /** Round 125 - SlickPay API family ('merchant' | 'user'; '' for Stripe) and OUR checkout reference */
  apiMode?: string;
  ref?: string;
}

export interface PaymentFilters {
  gateway?: string;
  /** a group (paid | failed | pending | refunded) or a raw event type */
  status?: string;
  plan?: string;
  country?: string;
  specialty?: string;
  from?: string;
  to?: string;
  /** user id (exact), part of an email or part of the learner's name */
  q?: string;
  includeTest?: boolean;
  /** Round 125 - one learner's payments (uid, exact) */
  userId?: string;
  apiMode?: string;
  currency?: string;
  minAmount?: number;
  maxAmount?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  /** the whole filtered view, for the CSV export */
  all?: boolean;
}
export interface CurrencyTotals { paid: number; paidCount: number; refunded: number; refundedCount: number; failedCount: number; pendingCount: number; refusedCount?: number; errorCount?: number }
export interface BreakdownEntry {
  key: string; rows: number; statuses: Record<string, number>;
  byCurrency: Record<string, { paid: number; paidCount: number; refunded: number; refundedCount: number }>;
}
export interface PaymentBreakdowns { plan: BreakdownEntry[]; specialty: BreakdownEntry[]; gateway: BreakdownEntry[]; apiMode: BreakdownEntry[]; month: BreakdownEntry[] }
export interface AdminPaymentsResult {
  payments: AdminPayment[]; total: number; page: number; limit: number;
  /** per currency - never mixed */
  totals: { byCurrency: Record<string, CurrencyTotals>; rows: number; testRowsHidden: number };
  facets: { gateways: string[]; plans: string[]; countries: string[]; specialties: string[]; currencies?: string[]; apiModes?: string[] };
  truncated: boolean;
  /** Round 125 - what was read: which sources hit the cap and how far back the view is complete */
  window?: { maxRows: number; cappedSources: string[]; completeFrom: string | null };
  /** rows per status group over the whole filtered set */
  statusCounts?: Record<string, number>;
  breakdowns?: PaymentBreakdowns;
}

export async function getAdminPayments(filters: PaymentFilters = {}): Promise<AdminPaymentsResult> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === '' || v === false || v === 'all') continue;
    params.append(k, v === true ? '1' : String(v));
  }
  return apiFetch<AdminPaymentsResult>(`/admin/payments?${params.toString()}`);
}

export interface AdminPaymentDetail {
  events: Array<{ id: string; timestamp: string | null; event: string; status: string; amount: number; currency: string; metadata: Record<string, unknown> }>;
  invoice: Record<string, unknown> | null;
  attempt: Record<string, unknown> | null;
  /** set for a TEST payment: the admin status page that shows what the gateway really said */
  testStatusPath: string | null;
}
/** One payment: its audit trail, the SlickPay invoice record and the checkout attempt (Round 125). */
export function getAdminPaymentDetail(p: { gateway?: string; ref?: string; id?: string }): Promise<AdminPaymentDetail> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) params.append(k, v);
  return apiFetch<AdminPaymentDetail>(`/admin/payments/detail?${params.toString()}`);
}

/**
 * The filtered payments as an Excel workbook or a printable PDF (Round 125). The server builds the file from the same filters as the list; this
 * fetches it with the admin's token and returns a Blob plus the suggested file name.
 */
export async function downloadAdminPaymentsReport(filters: PaymentFilters, format: 'xlsx' | 'pdf'): Promise<{ blob: Blob; fileName: string }> {
  const params = new URLSearchParams({ format });
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === '' || v === false || v === 'all' || k === 'page' || k === 'limit' || k === 'all') continue;
    params.append(k, v === true ? '1' : String(v));
  }
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}/admin/payments/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | undefined;
    try { const j = await res.json(); message = j.error || message; code = j.code; if (code === 'MFA_REQUIRED' && typeof window !== 'undefined') window.dispatchEvent(new Event(ADMIN_MFA_REQUIRED_EVENT)); } catch { /* not JSON */ }
    throw new ApiFetchError(message, res.status, code);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `payments.${format}`;
  return { blob: await res.blob(), fileName };
}

export interface RecentSlickPayTest { invoiceId: string; ref: string; createdAt: string | null; amountDZD: number; apiMode: 'merchant' | 'user'; state: string; statusPath: string | null }
/** The last 20 SlickPay TEST payments, newest first (Gateways -> SlickPay; super_admin). */
export const getRecentSlickPayTests = () => apiFetch<{ tests: RecentSlickPayTest[]; limit: number }>('/admin/gateways/slickpay/recent-tests');

// ── Admin: Audit Log ──────────────────────────────────────────────────────────

export interface AdminAuditLog {
  id: string;
  timestamp: string;
  action: string;
  adminUid: string;
  targetId: string;
  previousStatus?: string;
  diff?: any;
  reason?: string;
}

export async function getAdminAuditLog(filters: {
  adminUid?: string;
  action?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
} = {}): Promise<{ logs: AdminAuditLog[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams();
  if (filters.adminUid) params.append('adminUid', filters.adminUid);
  if (filters.action) params.append('action', filters.action);
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));

  return apiFetch<{ logs: AdminAuditLog[]; total: number; page: number; limit: number }>(
    `/admin/audit-log?${params.toString()}`
  );
}

// ── Admin: Subscription Grant/Revoke (Round 40, B3) ─────────────────────────

export async function grantSubscription(
  uid: string,
  payload: { planType: 'monthly' | 'block'; reason: string }
): Promise<{ uid: string; planType: string }> {
  return apiFetch<{ uid: string; planType: string }>(`/admin/users/${uid}/grant-subscription`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function revokeSubscription(
  uid: string,
  payload: { reason: string }
): Promise<{ uid: string; status: string }> {
  return apiFetch<{ uid: string; status: string }>(`/admin/users/${uid}/revoke-subscription`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── Admin: Pricing (Part C) ─────────────────────────────────────────────────

export interface PricingPlanCopy {
  name: string;
  period: string;
}

export interface PricingConfig {
  countryCurrencyMap: Record<string, ResolvedCheckoutCurrency>;
  currencies: Record<ResolvedCheckoutCurrency, Record<CheckoutPlanId, number | null>>;
  planCopy: Record<CheckoutPlanId, PricingPlanCopy | null>;
  /** Round 79, Part A — admin-selected, ordered feature-bullet identifiers per plan. null = use the default set/order. Identifiers only, never text — see constants/featureBullets.ts. */
  planFeatureBullets: Record<CheckoutPlanId, string[] | null>;
}

export type PricingConfigPatch = Partial<{
  countryCurrencyMap: Record<string, ResolvedCheckoutCurrency>;
  currencies: Partial<Record<ResolvedCheckoutCurrency, Partial<Record<CheckoutPlanId, number | null>>>>;
  planCopy: Partial<Record<CheckoutPlanId, PricingPlanCopy | null>>;
  planFeatureBullets: Partial<Record<CheckoutPlanId, string[] | null>>;
}>;

export async function getAdminPricingConfig(): Promise<PricingConfig> {
  return apiFetch<PricingConfig>('/admin/pricing');
}

export async function updateAdminPricingConfig(patch: PricingConfigPatch): Promise<PricingConfig> {
  return apiFetch<PricingConfig>('/admin/pricing', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// ── Admin: Roles (Round 39-B6c backend, Round 40-B2 frontend) ──────────────

export interface AdminUserRoleRow {
  uid: string;
  email: string | null;
  role: 'support' | 'admin' | 'super_admin' | null;
  assignedAt: string | null;
  assignedBy: string | null;
  /** Round 130: the staff member was invited but has never signed in -> "Resend invite" */
  name?: string | null;
  pendingInvite?: boolean;
}

export async function listAdminUsers(): Promise<{ users: AdminUserRoleRow[] }> {
  return apiFetch<{ users: AdminUserRoleRow[] }>('/admin/roles');
}

export async function assignAdminRole(
  uid: string,
  role: 'admin' | 'support'
): Promise<{ uid: string; role: string }> {
  return apiFetch<{ uid: string; role: string }>(`/admin/roles/${uid}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function revokeAdminRole(uid: string): Promise<{ uid: string }> {
  return apiFetch<{ uid: string }>(`/admin/roles/${uid}`, { method: 'DELETE' });
}

// Round 116 — super_admin resets another admin's two-factor enrolment (they must enrol again). Needs a written reason;
// the backend refuses your own row and any super_admin, verifies the factors are really gone, and writes an audit-log entry.
export async function resetAdminMfa(uid: string, reason: string): Promise<{ uid: string }> {
  return apiFetch<{ uid: string }>(`/admin/roles/${uid}/mfa/reset`, { method: 'POST', body: JSON.stringify({ reason }) });
}

// ── Admin: Gateway Credentials (Round 39-C4 backend, Round 40-B1 frontend) ──

export type PaymentGatewayId = 'stripe' | 'slickpay';

export interface GatewayCredentialFieldStatus {
  /** Round 117d - the Secret Manager name the field is stored in (a name, never the value). */
  secretId?: string;
  configured: boolean;
  configuredAt: string | null;
  configuredBy: string | null;
}

export interface GatewayCredentialsMetadata {
  gateway: PaymentGatewayId;
  fields: Record<string, GatewayCredentialFieldStatus>;
  lastTestedAt: string | null;
  lastTestResult: 'ok' | 'failed' | null;
  /** Round 117b - what the last test actually saw. The key itself is never part of any response. */
  lastTestHttpStatus?: number | null;
  lastTestMode?: 'production' | 'sandbox' | null;
  lastTestDetail?: string | null;
  /** Round 117d - Stripe only: card checkout is switched off, so the card is shown as On hold. */
  onHold?: boolean;
}

export async function getGatewayCredentialsStatus(gateway: PaymentGatewayId): Promise<GatewayCredentialsMetadata> {
  return apiFetch<GatewayCredentialsMetadata>(`/admin/gateways/${gateway}/credentials`);
}

export async function setGatewayCredential(
  gateway: PaymentGatewayId,
  field: string,
  value: string
): Promise<{ gateway: string; field: string; configured: boolean }> {
  return apiFetch<{ gateway: string; field: string; configured: boolean }>(`/admin/gateways/${gateway}/credentials`, {
    method: 'PUT',
    body: JSON.stringify({ field, value }),
  });
}

export interface GatewayTestResult { ok: boolean; detail?: string; httpStatus?: number; mode?: 'production' | 'sandbox'; onHold?: boolean }

export async function testGatewayConnection(gateway: PaymentGatewayId): Promise<GatewayTestResult> {
  return apiFetch<GatewayTestResult>(`/admin/gateways/${gateway}/test-connection`, {
    method: 'POST',
  });
}

// ── Admin: edit a learner's subscription (Round 117c, super_admin) ───────────
export interface SubscriptionEditInput {
  plan?: 'trial' | 'monthly' | 'block' | 'test';
  status?: 'active' | 'trial' | 'expired' | 'cancelled';
  startDate?: string;
  addDays?: number;
  cancel?: boolean;
}
export interface EntitlementSnapshot {
  plan: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  daysLeft: number | null;
  lessons: string | null;
}
export interface SubscriptionEditPreview {
  before: EntitlementSnapshot;
  after: EntitlementSnapshot;
  changes: string[];
  warnings: string[];
  status: string;
  /** hand back to apply: it fails if the subscription changed after the preview */
  token: string;
}

export async function previewSubscriptionEdit(uid: string, edit: SubscriptionEditInput): Promise<SubscriptionEditPreview> {
  return apiFetch<SubscriptionEditPreview>(`/admin/users/${encodeURIComponent(uid)}/subscription/preview`, { method: 'POST', body: JSON.stringify(edit) });
}

export async function applySubscriptionEdit(uid: string, body: SubscriptionEditInput & { reason: string; token: string }): Promise<{ before: EntitlementSnapshot; after: EntitlementSnapshot; changes: string[]; status: string }> {
  return apiFetch(`/admin/users/${encodeURIComponent(uid)}/subscription/apply`, { method: 'POST', body: JSON.stringify(body) });
}

// ── Bulk tester access (Round 121, super_admin) ────────────────────────────────────
export type TesterRowStatus = 'ready' | 'no_account' | 'blocked' | 'invalid' | 'duplicate';
export interface TesterPreview { plan: 'monthly' | 'block'; days: number | null; rows: Array<{ email: string; status: TesterRowStatus; uid?: string; note: string }>; counts: Record<TesterRowStatus, number>; token: string }
export interface TesterApplyResult { granted: Array<{ email: string; uid: string }>; failed: Array<{ email: string; error: string }>; skipped: number; batch: string }
export const previewTesterAccess = (b: { emails: string; plan: string; days?: number }) => apiFetch<TesterPreview>('/admin/testers/preview', { method: 'POST', body: JSON.stringify(b) });
export const applyTesterAccess = (b: { emails: string; plan: string; days?: number; reason: string; token: string }) => apiFetch<TesterApplyResult>('/admin/testers/apply', { method: 'POST', body: JSON.stringify(b) });

// ── Learner inbox (Round 120) ───────────────────────────────────────────────────────
export interface InboxMessage {
  id: string; type: string; title: string; body: string; lang: 'en' | 'ar'; createdAt: string; read: boolean; ticketId: string | null; fromRole: 'support' | 'system';
}
export interface InboxTicket {
  reportId: string; createdAt: string; description: string; status: string;
  thread: Array<{ id: string; from: 'learner' | 'support'; body: string; at: string }>;
}
export const getInbox = () => apiFetch<{ messages: InboxMessage[]; unread: number }>('/inbox');
export const getInboxUnread = () => apiFetch<{ unread: number }>('/inbox/unread-count');
export const markInboxRead = (id: string) => apiFetch<{ id: string }>(`/inbox/${encodeURIComponent(id)}/read`, { method: 'POST', body: '{}' });
export const markAllInboxRead = () => apiFetch<{ marked: number }>('/inbox/read-all', { method: 'POST', body: '{}' });
export const getInboxTicket = (reportId: string) => apiFetch<InboxTicket>(`/inbox/tickets/${encodeURIComponent(reportId)}`);
export const replyToInboxTicket = (reportId: string, body: string) =>
  apiFetch<{ id: string }>(`/inbox/tickets/${encodeURIComponent(reportId)}/reply`, { method: 'POST', body: JSON.stringify({ body }) });

// ── Managing a learner (Round 119): CEFR levels, name / email, staff invite ───────
export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1';
export type LevelSkill = 'reading' | 'listening' | 'writing' | 'speaking';
export interface LevelBreakdown {
  overall: { level: CefrLevel | null; rule: string };
  skills: Record<LevelSkill, { level: CefrLevel | null; source: 'placement' | 'progression' | 'admin_override' | null; at: string | null; by: string | null }>;
  block: CefrLevel | null;
  versioned: boolean;
  history: Array<{ at: string; kind: 'placement' | 'progression' | 'admin_override'; skill: LevelSkill | 'all' | 'overall'; from: string | null; to: string | null; by: string | null; reason: string | null }>;
  token: string;
}
export interface LevelOverridePreview {
  before: { skills: Record<LevelSkill, CefrLevel | null>; overall: CefrLevel | null };
  after: { skills: Record<LevelSkill, CefrLevel>; overall: CefrLevel };
  changes: string[];
  warnings: string[];
  blockChanges: boolean;
  token: string;
}
/** Round 122 - one checkout attempt (SlickPay / Stripe), including the ones the gateway refused before an invoice existed. */
export interface PaymentAttemptRecord {
  id: string; userId: string; name: string; email: string; phone: string; country: string; specialty: string;
  gateway: 'slickpay' | 'stripe'; operation: string; plan: string; amount: number | null; currency: string;
  outcome: 'created' | 'refused' | 'error'; gatewayMessage: string; gatewayStatus: number | null;
  isTest: boolean; initiatedBy: 'learner' | 'admin'; ref: string; invoiceId: string; createdAt: string; createdAtMs: number;
}
export const getLearnerPaymentAttempts = (uid: string) => apiFetch<{ attempts: PaymentAttemptRecord[] }>(`/admin/users/${encodeURIComponent(uid)}/payment-attempts`);
export const getLearnerLevels = (uid: string) => apiFetch<LevelBreakdown>(`/admin/users/${encodeURIComponent(uid)}/levels`);
export const previewLearnerLevels = (uid: string, levels: Record<LevelSkill, CefrLevel>) =>
  apiFetch<LevelOverridePreview>(`/admin/users/${encodeURIComponent(uid)}/levels/preview`, { method: 'POST', body: JSON.stringify({ levels }) });
export const applyLearnerLevels = (uid: string, levels: Record<LevelSkill, CefrLevel>, reason: string, token: string) =>
  apiFetch<{ before: Record<string, string | null>; after: Record<string, string>; overall: CefrLevel; blockChanged: boolean }>(`/admin/users/${encodeURIComponent(uid)}/levels/apply`, { method: 'POST', body: JSON.stringify({ levels, reason, token }) });

// Round 151 (B1) - ask a learner to redo the placement test for chosen skills (super admin; preview -> confirm; cancellable)
export interface PlacementRedoStatus {
  redo: { skills: LevelSkill[]; pending: LevelSkill[]; requestedAt: string } | null;
  locks: Partial<Record<LevelSkill, string>>;
  levels: Partial<Record<LevelSkill, string | null>>;
  hasStartedLessons: boolean;
}
export interface PlacementRedoPreview {
  skills: LevelSkill[]; keeps: LevelSkill[];
  current: Record<LevelSkill, { level: string | null; locked: boolean }>;
  abandons: { skill: LevelSkill; attemptId: string; answered: number }[];
  hasStartedLessons: boolean; warnings: string[]; token: string;
}
export const getPlacementRedo = (uid: string) => apiFetch<PlacementRedoStatus>(`/admin/users/${encodeURIComponent(uid)}/placement-redo`);
export const previewPlacementRedo = (uid: string, skills: LevelSkill[]) =>
  apiFetch<PlacementRedoPreview>(`/admin/users/${encodeURIComponent(uid)}/placement-redo/preview`, { method: 'POST', body: JSON.stringify({ skills }) });
export const applyPlacementRedo = (uid: string, skills: LevelSkill[], reason: string, token: string) =>
  apiFetch<{ redo: unknown; abandonedAttemptIds: string[] }>(`/admin/users/${encodeURIComponent(uid)}/placement-redo/apply`, { method: 'POST', body: JSON.stringify({ skills, reason, token }) });
export const cancelPlacementRedo = (uid: string, reason: string) =>
  apiFetch<{ restoredSkills: LevelSkill[]; abandonedAttemptIds: string[] }>(`/admin/users/${encodeURIComponent(uid)}/placement-redo/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });

export interface LearnerAccountInfo { name: string | null; email: string | null; emailVerified: boolean; providers: string[]; emailEditable: boolean; emailManagedBy: string | null }
export const getLearnerAccount = (uid: string) => apiFetch<LearnerAccountInfo>(`/admin/users/${encodeURIComponent(uid)}/account`);
export const setLearnerName = (uid: string, name: string) => apiFetch<{ name: string; previous: string | null }>(`/admin/users/${encodeURIComponent(uid)}/name`, { method: 'PUT', body: JSON.stringify({ name }) });
export const setLearnerEmail = (uid: string, email: string) =>
  apiFetch<{ email: string; previous: string | null; verification: { emailSent: boolean; link: string | null } }>(`/admin/users/${encodeURIComponent(uid)}/email`, { method: 'PUT', body: JSON.stringify({ email }) });

export interface StaffInviteResult { uid: string; email: string; role: string; previousRole: string | null; createdAuthUser: boolean; unchanged: boolean; signInLink: string | null; emailSent: boolean; next: string }
export const inviteStaffMember = (input: { email: string; name: string; role: 'support' | 'admin' | 'super_admin'; confirm?: string }) =>
  apiFetch<StaffInviteResult>('/admin/roles/invite', { method: 'POST', body: JSON.stringify(input) });

export interface ResendInviteResult { uid: string; email: string; emailSent: boolean; signInLink: string | null; next: string }
export const resendStaffInvite = (uid: string) =>
  apiFetch<ResendInviteResult>(`/admin/roles/${encodeURIComponent(uid)}/resend-invite`, { method: 'POST', body: JSON.stringify({}) });
// Round 130 (R130-4 / R130-3): delete from one's own view; the learner's own email choice
export const deleteInboxMessage = (id: string) => apiFetch<{ id: string }>(`/inbox/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const deleteInboxTicket = (reportId: string) => apiFetch<{ removedMessages: number }>(`/inbox/tickets/${encodeURIComponent(reportId)}`, { method: 'DELETE' });
export const getEmailPreferences = () => apiFetch<{ studyReminders: boolean }>('/inbox/preferences');
export const setEmailPreferences = (studyReminders: boolean) => apiFetch<{ studyReminders: boolean }>('/inbox/preferences', { method: 'PUT', body: JSON.stringify({ studyReminders }) });

// ── SlickPay TEST payment (Round 117b, super_admin) ─────────────────────────
export async function startSlickPayTestPayment(phone: string): Promise<{ invoiceId: number; paymentUrl: string; ref: string }> {
  return apiFetch('/admin/gateways/slickpay/test-payment', { method: 'POST', body: JSON.stringify({ phone }) });
}

/** Round 123b - the SlickPay API family and the bank account (masked) that user-mode payouts go to */
export interface SlickPayModeStatus {
  mode: 'merchant' | 'user';
  updatedAt: string | null;
  updatedBy: string | null;
  account: { count: number; receiving: string | null; isDefault: boolean } | null;
  accountProblem: string | null;
  confirmations: { merchant: string; user: string };
}
export const getSlickPayMode = () => apiFetch<SlickPayModeStatus>('/admin/gateways/slickpay/mode');
export const setSlickPayMode = (mode: 'merchant' | 'user', confirmation: string) =>
  apiFetch<SlickPayModeStatus>('/admin/gateways/slickpay/mode', { method: 'POST', body: JSON.stringify({ mode, confirmation }) });

export interface SlickPayTestPaymentStatus {
  invoiceId: string;
  /** Round 123b */
  apiMode?: 'merchant' | 'user';
  payoutAccount?: string | null;
  ref: string | null;
  state: SlickPayPaymentState;
  resolved: boolean;
  checkFailed: boolean;
  status: string;
  environment: 'production' | 'sandbox' | null;
  amountDZD: number;
  createdAtMs: number | null;
  lastCheckedAtMs: number | null;
  resolvedAtMs: number | null;
  observed: { state: string | null; amount: number | null; transactionAmount: number | null; commission?: number | null; total?: number | null; commissionDZD?: number | null; noteMatches: boolean | null; anomalies: string[]; keys: string[] };
  mismatchReason: string | null;
  blockedMessage: string | null;
  lastError: string | null;
  activation: { status: string | null; planType: string | null; planEndDate: string | null; appliedByThisPayment: boolean } | null;
}

export async function checkSlickPayTestPayment(params: { ref?: string } = {}): Promise<SlickPayTestPaymentStatus> {
  return apiFetch<SlickPayTestPaymentStatus>('/admin/gateways/slickpay/test-payment/check', { method: 'POST', body: JSON.stringify(params.ref ? { ref: params.ref } : {}) });
}

// ── Problem Reports (Round 57, B) ───────────────────────────────────────────────

export interface ConsoleLogEntry {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: string;
}

export interface SubmitProblemReportPayload {
  reportId: string;
  description: string;
  url: string;
  screenshotPath: string | null;
  attachmentPaths: string[];
  /** Round 129b: files that were not attached, and why - shown to staff on the ticket */
  refusedAttachments?: Array<{ name: string; reason: 'type' | 'size' | 'count' | 'upload_failed' }>;
  consoleLog: ConsoleLogEntry[];
}

export async function submitProblemReport(payload: SubmitProblemReportPayload): Promise<{ reportId: string }> {
  return apiFetch<{ reportId: string }>('/problem-reports', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
